import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronRight,
  ChevronDown,
  Settings,
  User,
  Bot,
  Mic,
  Sparkles,
  ArrowRight,
  Phone,
  MessageSquare,
  Code,
  Headphones,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../supabase/auth";
import AnimatedVoiceInteraction from "../AnimatedVoiceInteraction";
import PublicNavbar from "@/components/layout/PublicNavbar";

export default function LandingPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-agent-background text-agent-text">
      {/* Use PublicNavbar instead of inline navbar */}
      <PublicNavbar />

      <main className="pt-16">
        {/* Hero section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            <div className="lg:w-1/2 space-y-6">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-agent-primary/10 text-agent-primary text-sm font-medium mb-4">
                <Sparkles className="h-4 w-4 mr-2" />
                Build AI agents in minutes
              </div>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
                <span className="text-agent-primary">VOXI</span> for your
                business
              </h1>
              <p className="text-xl text-agent-muted max-w-2xl">
                Create custom AI agents that can talk to your customers, answer
                questions, and handle tasks - all with natural voice
                conversations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/signup">
                  <Button className="bg-agent-primary hover:bg-agent-primary/80 text-black font-medium px-8 py-6 text-lg w-full sm:w-auto">
                    Start Building
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/assistant-dashboard">
                  <Button
                    variant="outline"
                    className="border-agent-border text-agent-text hover:bg-agent-card hover:text-agent-primary px-8 py-6 text-lg w-full sm:w-auto"
                  >
                    Try Demo
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:w-1/2 relative">
              <AnimatedVoiceInteraction />
              <div className="absolute -bottom-4 -right-4 h-24 w-24 bg-agent-accent/10 rounded-full blur-xl"></div>
              <div className="absolute -top-4 -left-4 h-24 w-24 bg-agent-primary/10 rounded-full blur-xl"></div>
            </div>
          </div>
        </section>

        {/* Features section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              <span className="text-agent-primary">Powerful</span> Features
            </h2>
            <p className="text-xl text-agent-muted max-w-3xl mx-auto">
              Everything you need to build and deploy conversational AI agents
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-agent-card border border-agent-border rounded-xl p-6 hover:border-agent-primary transition-colors">
              <div className="h-12 w-12 bg-agent-primary/10 rounded-lg flex items-center justify-center mb-4">
                <Mic className="h-6 w-6 text-agent-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                Natural Voice Conversations
              </h3>
              <p className="text-agent-muted">
                Create AI agents with natural-sounding voices that can
                understand context and respond intelligently.
              </p>
            </div>

            <div className="bg-agent-card border border-agent-border rounded-xl p-6 hover:border-agent-primary transition-colors">
              <div className="h-12 w-12 bg-agent-secondary/10 rounded-lg flex items-center justify-center mb-4">
                <Bot className="h-6 w-6 text-agent-secondary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Advanced AI Models</h3>
              <p className="text-agent-muted">
                Powered by the latest AI models including GPT-4o, Claude, and
                more for intelligent conversations.
              </p>
            </div>

            <div className="bg-agent-card border border-agent-border rounded-xl p-6 hover:border-agent-primary transition-colors">
              <div className="h-12 w-12 bg-agent-accent/10 rounded-lg flex items-center justify-center mb-4">
                <Code className="h-6 w-6 text-agent-accent" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No-Code Builder</h3>
              <p className="text-agent-muted">
                Create and customize AI agents without writing a single line of
                code using our intuitive dashboard.
              </p>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-agent-card border border-agent-border rounded-xl p-8 hover:border-agent-primary transition-colors">
              <div className="flex items-center mb-6">
                <div className="h-10 w-10 bg-agent-primary/10 rounded-lg flex items-center justify-center mr-4">
                  <Headphones className="h-5 w-5 text-agent-primary" />
                </div>
                <h3 className="text-xl font-semibold">Customer Service</h3>
              </div>
              <p className="text-agent-muted mb-6">
                Deploy AI agents that can handle customer inquiries 24/7,
                reducing wait times and improving satisfaction.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <div className="h-5 w-5 rounded-full bg-agent-secondary/20 flex items-center justify-center mr-3">
                    <svg
                      width="12"
                      height="9"
                      viewBox="0 0 12 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 4L4.5 7.5L11 1"
                        stroke="#39ff14"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-sm">24/7 availability</span>
                </li>
                <li className="flex items-center">
                  <div className="h-5 w-5 rounded-full bg-agent-secondary/20 flex items-center justify-center mr-3">
                    <svg
                      width="12"
                      height="9"
                      viewBox="0 0 12 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 4L4.5 7.5L11 1"
                        stroke="#39ff14"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-sm">Instant responses</span>
                </li>
                <li className="flex items-center">
                  <div className="h-5 w-5 rounded-full bg-agent-secondary/20 flex items-center justify-center mr-3">
                    <svg
                      width="12"
                      height="9"
                      viewBox="0 0 12 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 4L4.5 7.5L11 1"
                        stroke="#39ff14"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-sm">Multilingual support</span>
                </li>
              </ul>
            </div>

            <div className="bg-agent-card border border-agent-border rounded-xl p-8 hover:border-agent-primary transition-colors">
              <div className="flex items-center mb-6">
                <div className="h-10 w-10 bg-agent-accent/10 rounded-lg flex items-center justify-center mr-4">
                  <Phone className="h-5 w-5 text-agent-accent" />
                </div>
                <h3 className="text-xl font-semibold">Sales & Outreach</h3>
              </div>
              <p className="text-agent-muted mb-6">
                Create AI agents that can qualify leads, schedule appointments,
                and follow up with prospects automatically.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <div className="h-5 w-5 rounded-full bg-agent-accent/20 flex items-center justify-center mr-3">
                    <svg
                      width="12"
                      height="9"
                      viewBox="0 0 12 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 4L4.5 7.5L11 1"
                        stroke="#ff00ff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-sm">Lead qualification</span>
                </li>
                <li className="flex items-center">
                  <div className="h-5 w-5 rounded-full bg-agent-accent/20 flex items-center justify-center mr-3">
                    <svg
                      width="12"
                      height="9"
                      viewBox="0 0 12 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 4L4.5 7.5L11 1"
                        stroke="#ff00ff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-sm">Appointment scheduling</span>
                </li>
                <li className="flex items-center">
                  <div className="h-5 w-5 rounded-full bg-agent-accent/20 flex items-center justify-center mr-3">
                    <svg
                      width="12"
                      height="9"
                      viewBox="0 0 12 9"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 4L4.5 7.5L11 1"
                        stroke="#ff00ff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-sm">Automated follow-ups</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link to="/assistant-dashboard">
              <Button className="bg-agent-primary hover:bg-agent-primary/80 text-black font-medium px-8 py-6 text-lg">
                Try the Agent Creator
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>

        {/* CTA section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto bg-gradient-to-r from-agent-primary/20 to-agent-accent/20 rounded-2xl p-1">
            <div className="bg-agent-card rounded-xl p-8 md:p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to build your{" "}
                <span className="text-agent-primary">AI agent</span>?
              </h2>
              <p className="text-xl text-agent-muted max-w-2xl mx-auto mb-8">
                Get started for free and create your first AI agent in minutes.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/signup">
                  <Button className="bg-agent-primary hover:bg-agent-primary/80 text-black font-medium px-8 py-6 text-lg w-full sm:w-auto">
                    Start Building
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/">
                  <Button
                    variant="outline"
                    className="border-agent-border text-agent-text hover:bg-agent-card hover:text-agent-primary px-8 py-6 text-lg w-full sm:w-auto"
                  >
                    Contact Sales
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-agent-border py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-medium text-agent-primary mb-4">Product</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/"
                    className="text-agent-muted hover:text-agent-primary transition-colors"
                  >
                    Features
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-agent-muted hover:text-agent-primary transition-colors"
                  >
                    Pricing
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-agent-muted hover:text-agent-primary transition-colors"
                  >
                    Integrations
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-agent-muted hover:text-agent-primary transition-colors"
                  >
                    Changelog
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-agent-primary mb-4">Resources</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/"
                    className="text-agent-muted hover:text-agent-primary transition-colors"
                  >
                    Documentation
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-agent-muted hover:text-agent-primary transition-colors"
                  >
                    API Reference
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-agent-muted hover:text-agent-primary transition-colors"
                  >
                    Tutorials
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-agent-muted hover:text-agent-primary transition-colors"
                  >
                    Blog
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-agent-primary mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/"
                    className="text-agent-muted hover:text-agent-primary transition-colors"
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-agent-muted hover:text-agent-primary transition-colors"
                  >
                    Careers
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-agent-muted hover:text-agent-primary transition-colors"
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-agent-muted hover:text-agent-primary transition-colors"
                  >
                    Partners
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-agent-primary mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/"
                    className="text-agent-muted hover:text-agent-primary transition-colors"
                  >
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-agent-muted hover:text-agent-primary transition-colors"
                  >
                    Terms
                  </Link>
                </li>
                <li>
                  <Link
                    to="/"
                    className="text-agent-muted hover:text-agent-primary transition-colors"
                  >
                    Security
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-agent-border flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <Link to="/" className="font-bold text-xl text-agent-primary">
                VOXI
              </Link>
            </div>
            <p className="text-agent-muted text-sm">
              © {new Date().getFullYear()} Tempo Labs. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
